import { useMemo, useState } from 'react';
import type { KeyboardEvent, MouseEvent, SyntheticEvent } from 'react';
import { createFilterOptions } from '@mui/material/Autocomplete';
import type { AutocompleteInputChangeReason } from '@mui/material/Autocomplete';

import { SELECT_TAG_CREATE_KEYS } from 'ui/select/select.constants';
import {
  getOptionSearchText,
  makeCreateOption,
  resolveSelectValue,
  toOptionList,
  toPlainOption,
  withSelectedOptions,
} from 'ui/select/select.helpers';
import type { SelectChangeValue, SelectOption } from 'ui/select/select.types';

const filterByLabelAndValue = createFilterOptions<SelectOption>({
  stringify: getOptionSearchText,
});

type SelectFilterState = Parameters<typeof filterByLabelAndValue>[1];

type SelectKeyboardEvent = KeyboardEvent<HTMLDivElement> & {
  defaultMuiPrevented?: boolean;
};

type InputMouseDownHandler = (event: MouseEvent<HTMLInputElement>) => void;

const PRIMARY_MOUSE_BUTTON = 0;

interface UseSelectInput {
  options?: SelectOption[];
  value?: unknown;
  onChange?: (value: SelectChangeValue<SelectOption>) => void;
  onBlur?: () => void;
  clearInputErr?: () => void;
  hasError: boolean;
  multiple: boolean;
  creatable: boolean;
  searchable: boolean;
}

export interface UseSelectResult {
  selected: SelectOption | SelectOption[] | null;
  autocompleteOptions: SelectOption[];
  inputValue: string;
  isTagInput: boolean;
  hasValue: boolean;
  filterOptions: typeof filterByLabelAndValue;
  handleChange: (
    event: SyntheticEvent,
    value: SelectOption | SelectOption[] | null
  ) => void;
  handleInputChange: (
    event: SyntheticEvent,
    value: string,
    reason: AutocompleteInputChangeReason
  ) => void;
  handleKeyDown: (event: SelectKeyboardEvent) => void;
  handleBlur: () => void;
  isOpen: boolean;
  handleOpen: () => void;
  handleClose: () => void;
  getInputMouseDown: (
    muiHandler?: InputMouseDownHandler
  ) => InputMouseDownHandler | undefined;
}

export const useSelect = ({
  options,
  value,
  onChange,
  onBlur,
  clearInputErr,
  hasError,
  multiple,
  creatable,
  searchable,
}: UseSelectInput): UseSelectResult => {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const isTagInput = multiple && creatable && !options;
  const selected = useMemo(
    () => resolveSelectValue(value, options ?? [], multiple),
    [value, options, multiple]
  );
  const selectedList = toOptionList(selected);
  const autocompleteOptions = withSelectedOptions(options ?? [], selectedList);

  const emit = (next: SelectChangeValue<SelectOption>) => {
    if (hasError) clearInputErr?.();
    onChange?.(next);
  };

  const addTag = () => {
    setInputValue('');
    emit([...selectedList, { label: inputValue, value: inputValue }]);
  };

  const filterOptions = (list: SelectOption[], state: SelectFilterState) => {
    const filtered = filterByLabelAndValue(list, state);
    const typed = state.inputValue.trim().toLowerCase();

    if (!creatable || isTagInput || !typed) return filtered;

    const exists = list.some((option) => option.label.toLowerCase() === typed);

    return exists
      ? filtered
      : [...filtered, makeCreateOption(state.inputValue)];
  };

  const handleChange = (
    _event: SyntheticEvent,
    next: SelectOption | SelectOption[] | null
  ) => {
    if (Array.isArray(next)) {
      emit(next.map(toPlainOption));

      return;
    }

    emit(next ? toPlainOption(next) : null);
  };

  const handleInputChange = (
    _event: SyntheticEvent,
    next: string,
    reason: AutocompleteInputChangeReason
  ) => {
    if (isTagInput && reason === 'reset') return;

    setInputValue(next);
    if (isTagInput && hasError && reason === 'input') clearInputErr?.();
  };

  const handleKeyDown = (event: SelectKeyboardEvent) => {
    if (!isTagInput || !inputValue || !SELECT_TAG_CREATE_KEYS.has(event.key)) {
      return;
    }

    event.preventDefault();
    event.defaultMuiPrevented = true;
    addTag();
  };

  const handleBlur = () => {
    if (isTagInput && inputValue) addTag();
    onBlur?.();
  };

  const handleOpen = () => setIsOpen(true);

  const handleClose = () => setIsOpen(false);

  const getInputMouseDown = (muiHandler?: InputMouseDownHandler) => {
    if (searchable) return muiHandler;

    return (event: MouseEvent<HTMLInputElement>) => {
      if (isOpen && event.button === PRIMARY_MOUSE_BUTTON) {
        handleClose();

        return;
      }

      muiHandler?.(event);
    };
  };

  return {
    selected,
    autocompleteOptions,
    inputValue,
    isTagInput,
    hasValue: !!selectedList.length,
    filterOptions,
    handleChange,
    handleInputChange,
    handleKeyDown,
    handleBlur,
    isOpen,
    handleOpen,
    handleClose,
    getInputMouseDown,
  };
};
